import React from "react";
import { SimpleGrid, Text } from "@chakra-ui/react";
import { type GetStaticProps, type NextPage } from "next";
import {
  type Banner,
  type IntegrationSimple,
  type Page as SanityPage,
  getPage,
  getAnnouncementBanner,
  getSimpleIntegrations,
  type Customer,
  getStackedLogos,
} from "lib/sanity";
import { HalfHero } from "components/half-hero";
import { Section } from "components/section";
import { Page } from "components/page";
import { Feature } from "components/feature";
import { Bullets } from "components/bullets";
import DestinationMarquee from "components/destination-marquee";
import { DemoEmbedHalf } from "components/demo-embed";
import { getPageMetaImage } from "utils";
import { PairImage } from "components/pair-image";
import { StackedLogos } from "components/logos";
import heroImage from "../../../public/assets/solutions/operations/hero.png";
import firstPairImage from "../../../public/assets/solutions/operations/pair-1.svg";
import secondPairImage from "../../../public/assets/solutions/operations/pair-2.svg";
import thirdPairImage from "../../../public/assets/solutions/operations/pair-3.svg";

type PageProps = {
  page: SanityPage;
  destinations: IntegrationSimple[];
  announcementBanner?: Banner;
  customerRows: Customer[][];
};

const Operations: NextPage<PageProps> = ({ page, destinations, announcementBanner, customerRows }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        hideSecondary
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={3 / 2}
        imageObjectFit="contain"
      />

      <StackedLogos title="Trusted by operations teams at" customerRows={customerRows} />

      <Section center>
        <SimpleGrid columns={[1, 1, 2, 4]} width="100%" gap={12} justifyItems="center">
          <Feature
            center
            title="Automate"
            image="/assets/solutions/operations/feature-1.svg"
            description="Streamline and automate the sales process to improve efficiency and effectiveness."
          />
          <Feature
            center
            title="Sync"
            image="/assets/solutions/operations/feature-2.svg"
            description="Get any important data like lead scores, churn risks, and contact behavior into any tool."
          />
          <Feature
            center
            title="Alert"
            image="/assets/solutions/operations/feature-3.svg"
            description="Quickly identify and address any operational issues."
          />
          <Feature
            center
            title="Identify"
            image="/assets/solutions/operations/feature-4.svg"
            description="Identify new opportunities for business growth and revenue expansion."
          />
        </SimpleGrid>
      </Section>

      <DemoEmbedHalf
        eyebrow="#1 Reverse ETL"
        title="Start syncing data in minutes"
        description={
          <Text>
            Your data is ready to use in your warehouse or BI tool. Use Hightouch to sync that data to sales and
            marketing tools without APIs, CSVs, or engineering favors.
          </Text>
        }
      />

      <Section>
        <PairImage
          align="left"
          image={firstPairImage}
          title="Build a 360˚ customer view in your existing tools"
          description={
            <Bullets
              textAlign="left"
              items={[
                "Bring the data that matters to the tools your team already loves like your CRM, comms, and project management tools.",
                "Leverage ongoing data team work and bring any data from your tools to your CRM.",
                "No need to train everyone on SQL, Looker, and spreadsheets. Sync the data that matters into Salesforce without engineers.",
              ]}
            />
          }
        />
      </Section>

      <Section>
        <PairImage
          align="right"
          image={secondPairImage}
          title="Lead scoring with endless flexibility"
          description={
            <Bullets
              textAlign="left"
              items={[
                "Lead scores are unique to your business. Out-of-the-box solutions lack the full picture. Your data warehouse has all of your customer data and touchpoints.",
                "Build product and marketing qualified lead models that make sense for your business and sync them to your CRM.",
                "Iterate on top of all of the customer data in your warehouse without waiting on engineers.",
              ]}
            />
          }
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={thirdPairImage}
          actionText="Get started"
          title="Stop worrying about integration problems"
          description={
            <Bullets
              textAlign="left"
              items={[
                "iPaaS platforms were not built for data pipelines. Save time and headaches with a purpose-built tool.",
                "Build what you want your data to look like, and stop worrying about how.",
                "Our proprietary query engine lets you run SQL directly on top of BI, Google Sheets, Airtable, and Salesforce including Joins.",
              ]}
            />
          }
        />
      </Section>
      <DestinationMarquee destinations={destinations} />
    </Page>
  );
};

export default Operations;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, destinations, announcementBanner, customerRows] = await Promise.all([
    getPage("/solutions/operations"),
    getSimpleIntegrations("destination"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      destinations,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};
